import { computed } from 'vue'
import { RouteLocationRaw, useRouter } from 'vue-router'

import PGFaviconSVG from '@/assets/icons/favicon.svg?file'
import { AvailableLocale, mapLocaleToHrefLang } from '@/i18n/helpers'
import { useI18nAddon } from '@/i18n/i18n'
import { ROUTE_NAMES } from '@/router/constants'
import { RAW_SOCIAL_LINKS } from '@/router/navigation-constants'

import { useConfig } from './config'
import { useTr } from './use-tr'

export const SCHEMA_CURRENCY = 'USD'

export function useSchemaOrg() {
    const router = useRouter()
    const tr = useTr()
    const { currentLocale } = useI18nAddon()
    const { VUE_APP_ANDROID_APP_URL } = useConfig()
    const mainUrl = computed(() => getFullLink({ name: ROUTE_NAMES.MAIN }))

    function getFullLink(href: string | RouteLocationRaw) {
        if (typeof href === 'string') {
            return `https://playgama.com${href}`
        } else {
            return `https://playgama.com${router.resolve(href).fullPath}`
        }
    }

    function getInLanguage(locale?: AvailableLocale) {
        return mapLocaleToHrefLang(locale || currentLocale.value)
    }

    const isPartOf = computed(() => ({
        isPartOf: {
            '@type': 'WebSite',
            '@id': `${mainUrl.value}#website`,
            name: 'Playgama',
            url: mainUrl.value,
            description: tr('headMeta.description'),
            inLanguage: getInLanguage(),
            // Do not translate publisher - in uses for non-locale and locale pages as well
            publisher: {
                '@type': 'Organization',
                '@id': 'https://playgama.com/#organization',
                url: 'https://playgama.com',
                logo: PGFaviconSVG,
                name: 'Playgama',
                legalName: 'Playgama',
                description:
                    'Playgama is a fast-growing platform that connects HTML5 game developers with a global audience of over 300 million monthly players. Whether you’re looking to publish, monetize, or scale your game, Playgama gives you the tools and exposure to make it happen.',
                brand: {
                    '@type': 'Brand',
                    '@id': 'https://playgama.com/#brand',
                    name: 'Playgama',
                    slogan: 'Play, Share, Profit. Game on!',
                },
                email: 'support@playgama.com',
                sameAs: [...RAW_SOCIAL_LINKS, VUE_APP_ANDROID_APP_URL],
                contactPoint: {
                    '@type': 'ContactPoint',
                    contactType: 'User Feedback',
                    url: getFullLink({ name: ROUTE_NAMES.CONTACTS }),
                    email: 'support@playgama.com',
                },
                address: {
                    '@type': 'PostalAddress',
                    postalCode: '500001',
                    addressLocality: 'Dubai',
                    streetAddress: 'Nad Al Sheba',
                    addressCountry: {
                        '@type': 'Country',
                        name: 'AE',
                    },
                },
                location: {
                    '@type': 'PostalAddress',
                    postalCode: '500001',
                    addressLocality: 'Dubai',
                    streetAddress: 'Nad Al Sheba',
                    addressCountry: {
                        '@type': 'Country',
                        name: 'AE',
                    },
                },
                founder: [
                    {
                        '@type': 'Person',
                        name: 'Dmitry Kachmar',
                        jobTitle: 'Founder',
                        sameAs: ['https://www.linkedin.com/in/dmitry-kachmar-84392725/'],
                    },
                ],
            },
        },
    }))

    return {
        isPartOf,
        getFullLink,
        getInLanguage,
    }
}
